import {
  ArdoqId,
  PermissionAccessLevel,
  PrivilegeLabel,
} from '@ardoq/api-types';
import { permissionsOperations } from './permissionsOperations';
import { HasResourcePermissionFn, LoadedPermissionContext } from './types';

export type AccessSpec =
  | OrgPrivilegeCheckDescriptor
  | ResourcePermissionDescriptor
  | (ResourcePermissionDescriptor & OrgPrivilegeCheckDescriptor);

/**
 * Compile privilege check descriptors into runnable functions while
 * maintaining existing key-association.
 */
export function compileAccessSpecs<Keys extends string>(
  inputPrivileges: Record<Keys, AccessSpec | AccessSpec[]>
): Record<Keys, HasResourcePermissionFn> {
  const entries = Object.entries<AccessSpec | AccessSpec[]>(
    inputPrivileges
  ).map(([key, desc]) => {
    /**
     * Capture privilege descriptor in closure, but dynamically provide
     * context and resourceId at runtime.
     */
    const checkFn: HasResourcePermissionFn = (context, resourceId) => {
      if (!permissionsOperations.isLoadedPermissionContext(context)) {
        return false;
      }

      if (Array.isArray(desc)) {
        return desc.some(spec =>
          hasPrivilegeAndPermission(context, resourceId, spec)
        );
      }
      return hasPrivilegeAndPermission(context, resourceId, desc);
    };
    return [key, checkFn];
  });

  return Object.fromEntries(entries) as Record<Keys, HasResourcePermissionFn>;
}

const hasPrivilegeAndPermission = (
  context: LoadedPermissionContext,
  resourceId: ArdoqId,
  accessSpec: AccessSpec
) => {
  const hasPrivilege = validatePrivilege(context, accessSpec);
  const hasPermission = validateResourcePermission(
    context,
    resourceId,
    accessSpec
  );
  return hasPrivilege && hasPermission;
};

/**
 * Check if there's a privilege check defined, and run it if true
 */
const validatePrivilege = (
  context: LoadedPermissionContext,
  accessSpec: AccessSpec
) => {
  // if there's no privilege check defined we consider the validation passed
  if (!requiresOrgPrivilegeCheck(accessSpec)) {
    return true;
  }
  return runOrgPrivilegeCheck(context, accessSpec.orgPrivilege);
};

/**
 * Check if there's a permission check defined, and run it if true
 */
const validateResourcePermission = (
  context: LoadedPermissionContext,
  resourceId: ArdoqId,
  accessSpec: AccessSpec
) => {
  // If there's no permission check defined we consider the validation passed
  if (!requiresResourcePermissionCheck(accessSpec)) {
    return true;
  }
  return runResourcePermissionCheck(
    context,
    resourceId,
    accessSpec.resourcePermission
  );
};

// ============================================================================
// Privilege checks

type OrgPrivilegeCheckDescriptor = { orgPrivilege: PrivilegeLabel };

const requiresOrgPrivilegeCheck = (
  accessSpec: AccessSpec
): accessSpec is OrgPrivilegeCheckDescriptor => {
  return 'orgPrivilege' in accessSpec;
};

/**
 * Checking privilege is almost like a feature toggle, we're checking the
 * user is allowed to perform an action in general.
 * So it may be that they are generally allowed to edit workspaces, however
 * permissions would control if the user is allowed to edit any particular
 * workspace.
 */
const runOrgPrivilegeCheck = (
  context: LoadedPermissionContext,
  orgPrivilege: PrivilegeLabel
) => {
  const orgId = permissionsOperations.getOrgIdFromContext(context);
  const userPrivileges = context.user.privileges[orgId] ?? [];
  return userPrivileges.some(
    userPrivilege => userPrivilege.label === orgPrivilege
  );
};

// ============================================================================
// Resource permission checks

/**
 * Resource permission validate if the user has admin | edit | read permission
 * to a particular resource.
 */
type ResourcePermissionDescriptor = {
  resourcePermission: PermissionAccessLevel;
};

const requiresResourcePermissionCheck = (
  accessSpec: AccessSpec
): accessSpec is ResourcePermissionDescriptor => {
  return 'resourcePermission' in accessSpec;
};

const runResourcePermissionCheck = (
  context: LoadedPermissionContext,
  resourceId: ArdoqId,
  requiredPermissionLevel: PermissionAccessLevel
) => {
  if (requiredPermissionLevel === PermissionAccessLevel.ADMIN) {
    return permissionsOperations.hasAdminAccess(
      context.user,
      context.permissions,
      resourceId
    );
  }
  if (requiredPermissionLevel === PermissionAccessLevel.EDIT) {
    return permissionsOperations.hasEditAccess(
      context.user,
      context.permissions,
      resourceId
    );
  }
  if (requiredPermissionLevel === PermissionAccessLevel.READ) {
    return permissionsOperations.hasReadAccess(
      context.user,
      context.permissions,
      resourceId
    );
  }
  return false;
};
